<script setup>
import {ref, watch, computed} from 'vue'
import InputText from "@/components/common/fields/InputText.vue";

const props = defineProps({
    modelValue: String,
    label: String,
    confirmPassword: String
})

const emit = defineEmits(['update:modelValue'])

const internalPassword = ref(props.modelValue)

watch(() => props.modelValue, (val) => {
    internalPassword.value = val
})

watch(internalPassword, (val) => {
    emit('update:modelValue', val)
})

const showPassword = ref(false)
const togglePassword = () => {
    showPassword.value = !showPassword.value
}

const criteria = computed(() => {
    const pwd = internalPassword.value || ''
    return {
        length: pwd.length >= 8,
        uppercase: /[A-Z]/.test(pwd),
        lowercase: /[a-z]/.test(pwd),
        number: /\d/.test(pwd),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
        match: pwd === props.confirmPassword && pwd.length > 0
    }
})

const passwordStrength = computed(() => {
    const c = criteria.value
    const score = [c.uppercase, c.lowercase, c.number, c.special].filter(Boolean).length
    if (score <= 1) return 'weak'
    if (score === 2 || score === 3) return 'medium'
    if (score === 4 && c.length) return 'strong'
    return ''
})

defineExpose({
    passwordStrength,
    showPassword
})

</script>

<template>
    <div class="trezo-form-group">
        <div class="d-flex">
            <InputText
                v-model="internalPassword"
                :label="label"
                class="me-2"
                maxlength="30"
                :type="showPassword ? 'text' : 'password'"
                required
            />
            <span @click="togglePassword" class="align-icon material-icons align-content-center">
                {{ showPassword ? 'visibility_off' : 'visibility' }}
            </span>
        </div>
        <div class="mt-0 mb-4">
            <ul class="list-unstyled">
                <li :class="['d-flex align-items-center',  criteria.length ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.length ? 'check_circle' : 'cancel' }}
                    </span>
                    {{ $t('components.authentication.resetPassword.passwordLength') }}
                </li>
                <li :class="['d-flex align-items-center', criteria.uppercase ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.uppercase ? 'check_circle' : 'cancel' }}
                    </span>
                    {{ $t('components.authentication.resetPassword.uppercase') }}
                </li>
                <li :class="['d-flex align-items-center', criteria.lowercase ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.lowercase ? 'check_circle' : 'cancel' }}
                    </span>
                    {{ $t('components.authentication.resetPassword.lowercase') }}
                </li>
                <li :class="['d-flex align-items-center', criteria.number ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.number ? 'check_circle' : 'cancel' }}
                    </span>
                    {{ $t('components.authentication.resetPassword.number') }}
                </li>
                <li :class="['d-flex align-items-center', criteria.special ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.special ? 'check_circle' : 'cancel' }}
                    </span>
                    {{ $t('components.authentication.resetPassword.specialChar') }}
                </li>
                <li :class="['d-flex align-items-center', criteria.match ? 'text-success' : 'text-danger']">
                    <span class="material-icons me-1">
                        {{ criteria.match ? "check_circle" : "cancel" }}
                    </span>
                    {{ $t("components.authentication.resetPassword.passwordMatch") }}
                </li>
            </ul>
        </div>
    </div>
</template>

